import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { SignOut, MapPin, Camera, Clock, CheckCircle, WarningCircle, SignIn } from "@phosphor-icons/react";
import SelfieCapture from "@/components/punch/SelfieCapture";
import PunchLeave from "@/components/punch/PunchLeave";
import { fmt12 } from "@/components/hr/attendanceStatus";

const getPosition = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve(null);
  navigator.geolocation.getCurrentPosition(
    (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
    () => resolve(null),
    { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 },
  );
});

export default function PunchHome({ me, onRefresh, onLogout, busy }) {
  const [now, setNow] = useState(new Date(me.server_time));
  const [capture, setCapture] = useState(null); // "in" | "out"
  const [siteId, setSiteId] = useState(me.sites?.[0]?.id || "");
  const [geo, setGeo] = useState(undefined); // undefined=not asked, null=unavailable, {lat,lng}
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => { setNow(new Date(me.server_time)); }, [me.server_time]);
  useEffect(() => { const t = setInterval(() => setNow((n) => new Date(n.getTime() + 1000)), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { getPosition().then(setGeo); }, []);

  const today = me.today;
  const canIn = !today.in_time;
  const canOut = !!today.in_time;
  const s = me.summary;

  const nearest = useMemo(() => {
    if (!geo || !me.sites?.length) return null;
    const d = (a, b) => { const R = 6371000, toR = (x) => (x * Math.PI) / 180; const dp = toR(b.lat - a.lat), dl = toR(b.lng - a.lng); const h = Math.sin(dp / 2) ** 2 + Math.cos(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.sin(dl / 2) ** 2; return 2 * R * Math.asin(Math.sqrt(h)); };
    return me.sites.map((st) => ({ ...st, dist: d(geo, st) })).sort((a, b) => a.dist - b.dist)[0];
  }, [geo, me.sites]);

  useEffect(() => { if (nearest && !me.sites.find((x) => x.id === siteId)) setSiteId(nearest.id); }, [nearest, me.sites, siteId]);

  const doPunch = async (kind, photoBlob) => {
    setSending(true);
    try {
      const pos = geo === undefined ? await getPosition() : geo;
      const fd = new FormData();
      if (pos) { fd.append("lat", pos.lat); fd.append("lng", pos.lng); fd.append("accuracy", pos.accuracy ?? ""); }
      if (siteId) fd.append("site_id", siteId);
      if (photoBlob) fd.append("photo", photoBlob, "selfie.jpg");
      const { data } = await api.post(`/punch/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLastResult(data);
      toast.success(`${kind === "in" ? "Punched in" : "Punched out"} at ${fmt12(data.time)}${data.geo?.outside ? " · outside office (flagged)" : ""}`);
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSending(false); setCapture(null); }
  };

  const tz = me.timezone || "Asia/Kolkata";
  const timeStr = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true, timeZone: tz });
  const dateStr = now.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", timeZone: tz });
  const lateInfo = today.in_time ? (today.is_late ? `Late by ${today.late_min} min${today.forgiven ? " (forgiven)" : " · half-day"}` : today.late_min > 0 ? `${today.late_min} min late · within grace` : "On time") : null;

  return (
    <div className="flex-1 flex flex-col px-5 pt-6 pb-8" data-testid="punch-home">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#EA580C]">{me.employee.emp_code}{me.employee.designation ? ` · ${me.employee.designation}` : ""}</div>
          <div className="text-xl font-bold tracking-tight" data-testid="punch-emp-name">{me.employee.name}</div>
        </div>
        <button onClick={onLogout} disabled={busy} className="h-9 px-3 rounded-full border border-slate-300 text-[12px] flex items-center gap-1 text-slate-600 bg-white" data-testid="punch-logout"><SignOut size={14} /> Sign out</button>
      </div>

      <div className="mt-5 rounded-sm bg-[#0F172A] text-white px-5 py-5 relative overflow-hidden" data-testid="punch-clock-card">
        <div className="absolute -right-6 -bottom-8 w-40 h-40 rounded-full border-[14px] border-white/5" />
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-semibold">{dateStr}</div>
        <div className="text-[40px] leading-none font-bold font-mono mt-1 tabular-nums" data-testid="punch-clock">{timeStr}</div>
        <div className="text-[11px] text-white/60 mt-2">Shift {fmt12(me.shift.shift_in)} – {fmt12(me.shift.shift_out)} · grace {me.shift.late_grace_min} min · server time</div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-sm bg-white/5 px-3 py-2">
            <div className="text-[9.5px] uppercase tracking-[0.16em] text-white/50">Punch in</div>
            <div className="text-lg font-semibold font-mono" data-testid="punch-today-in">{today.in_time ? fmt12(today.in_time) : "—"}</div>
          </div>
          <div className="rounded-sm bg-white/5 px-3 py-2">
            <div className="text-[9.5px] uppercase tracking-[0.16em] text-white/50">Punch out</div>
            <div className="text-lg font-semibold font-mono" data-testid="punch-today-out">{today.out_time ? fmt12(today.out_time) : "—"}</div>
          </div>
        </div>
        {lateInfo && <div className={`mt-3 text-[12px] flex items-center gap-1.5 ${today.is_late && !today.forgiven ? "text-orange-300" : "text-emerald-300"}`} data-testid="punch-late-info">{today.is_late && !today.forgiven ? <WarningCircle size={14} /> : <CheckCircle size={14} />} {lateInfo}</div>}
      </div>

      <div className="mt-4 flex items-center gap-2 text-[12px] text-slate-600" data-testid="punch-location-status">
        <MapPin size={14} className={geo ? "text-emerald-600" : "text-slate-400"} />
        {geo === undefined ? "Getting your location…" : geo === null ? "Location unavailable — punch will be flagged 'no location'" : nearest ? `${Math.round(nearest.dist)} m from ${nearest.name}${nearest.dist > nearest.radius_m ? " · outside radius" : ""}` : "Location captured"}
      </div>
      {me.sites?.length > 1 && (
        <select value={siteId} onChange={(e) => setSiteId(e.target.value)} className="mt-2 h-10 rounded-sm border border-slate-300 bg-white px-2 text-[13px]" data-testid="punch-site-select">
          {me.sites.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
        </select>
      )}

      <div className="mt-5 grid grid-cols-2 gap-3">
        <button onClick={() => setCapture("in")} disabled={!canIn || sending} className="h-20 rounded-sm bg-[#EA580C] text-white font-semibold text-[16px] flex flex-col items-center justify-center gap-1 disabled:opacity-35 active:scale-[0.99] transition-transform" data-testid="punch-in-btn">
          <span className="flex items-center gap-2"><SignIn size={20} weight="bold" /> Punch In</span>
          <span className="text-[10px] font-normal opacity-80 flex items-center gap-1"><Camera size={11} /> selfie + location</span>
        </button>
        <button onClick={() => setCapture("out")} disabled={!canOut || sending} className="h-20 rounded-sm bg-white border-2 border-[#0F172A] text-[#0F172A] font-semibold text-[16px] flex flex-col items-center justify-center gap-1 disabled:opacity-35 active:scale-[0.99] transition-transform" data-testid="punch-out-btn">
          <span className="flex items-center gap-2"><SignOut size={20} weight="bold" /> Punch Out</span>
          <span className="text-[10px] font-normal opacity-70">{today.out_time ? "update out time" : "end your day"}</span>
        </button>
      </div>
      {sending && <div className="mt-2 text-center text-[12px] text-slate-500">Recording punch…</div>}

      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">{me.period_label} · your summary</div>
        <div className="grid grid-cols-4 gap-2" data-testid="punch-summary">
          <Stat label="Paid days" value={fmtNum(s.paid_days)} sub={`of ${s.days_in_month}`} tone={s.lop_days > 0 ? "rose" : "emerald"} />
          <Stat label="Leaves" value={fmtNum(s.leaves)} />
          <Stat label="Late" value={fmtNum(s.late_marks)} tone={s.late_marks ? "amber" : ""} />
          <Stat label="Half-days" value={fmtNum(s.half_days)} tone={s.half_days ? "amber" : ""} />
        </div>
      </div>

      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2 flex items-center gap-1"><Clock size={12} /> Recent days</div>
        <div className="rounded-sm border border-slate-200 bg-white divide-y divide-slate-100" data-testid="punch-history">
          {me.recent.map((d) => (
            <div key={d.day} className="flex items-center gap-3 px-3 py-2 text-[12.5px]">
              <span className="w-14 text-slate-500 font-mono">{d.weekday} {String(d.day).padStart(2, "0")}</span>
              <span className={`w-9 text-center rounded-sm text-[10px] font-bold py-0.5 ${badge(d)}`}>{d.unmarked ? "—" : d.effective_status}</span>
              <span className="flex-1 font-mono text-slate-700">{d.in_time ? fmt12(d.in_time) : "—"} → {d.out_time ? fmt12(d.out_time) : "—"}</span>
              {d.is_late && <span className={`text-[10px] font-semibold ${d.forgiven ? "text-slate-400" : "text-[#EA580C]"}`}>late {d.late_min}m</span>}
            </div>
          ))}
        </div>
      </div>

      <PunchLeave tz={tz} />

      {capture && (
        <SelfieCapture title={capture === "in" ? "Punch In" : "Punch Out"} onCapture={(blob) => doPunch(capture, blob)} onCancel={() => setCapture(null)} onSkip={() => doPunch(capture, null)} />
      )}
      {lastResult?.geo?.outside && <div className="mt-4 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid="punch-outside-note">Your last punch was {lastResult.geo.distance_m} m from {lastResult.geo.site_name} — it has been flagged for admin review.</div>}
    </div>
  );
}

const fmtNum = (v) => (Number.isInteger(Number(v)) ? String(v) : Number(v).toFixed(1));
const badge = (d) => d.unmarked ? "bg-slate-100 text-slate-400" : ({ P: "bg-emerald-50 text-emerald-700", OD: "bg-emerald-50 text-emerald-700", WO: "bg-slate-100 text-slate-500", H: "bg-sky-50 text-sky-700", HD: "bg-amber-50 text-amber-800", L: "bg-rose-50 text-rose-700", A: "bg-rose-100 text-rose-800" }[d.effective_status] || "bg-slate-100");

function Stat({ label, value, sub, tone }) {
  const cls = { rose: "text-rose-700", amber: "text-amber-700", emerald: "text-emerald-700" }[tone] || "text-slate-900";
  return (
    <div className="rounded-sm bg-white border border-slate-200 px-2 py-2">
      <div className="text-[9px] uppercase tracking-[0.14em] font-semibold text-slate-500">{label}</div>
      <div className={`text-lg font-bold font-mono ${cls}`}>{value}</div>
      {sub && <div className="text-[9.5px] text-slate-400">{sub}</div>}
    </div>
  );
}
